import { useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { usePageAccessGuard } from "@/hooks/usePageAccessGuard";
import { useSimplifiedUserData } from "@/providers/SimplifiedUserDataProvider";
import { UnifiedItemsContainer } from "@/components/layout/page-containers";
import { PageHandlers, TransformContext } from "@/components/layout/page-containers/UnifiedItemsContainer/types";
import { libraryConfig } from "./unified-items/configs/libraryConfig";
import { CardData } from "@/components/layout/element-layout/grid-layout/GridLayout";

// Import stores and types
import {
  objectsStore,
  ObjectItem,
  GlossaryItem,
  createObject,
  updateObject,
  deleteObject,
  fetchObjects,
  sectionDataObject,
} from "@/lib/stores/objectsStore";
import { objectsDataSource } from "@/lib/dataSources/sources/objects";
import GlossaryWidget from "@/components/cards/widgets/ObjectsWidget";

/**
 * LibraryPage - SOURCE OF TRUTH for library object operations
 * 
 * This page owns all library-specific:
 * - Store operations (write/update/delete in objectsStore)
 * - CRUD handlers (create, update, delete library items)
 * - Card transformation (raw ObjectItem → CardData)
 * - Wizard callbacks (what happens after create/update)
 * 
 * When the structure of library objects changes, update THIS file.
 */
export default function LibraryPage() {
  usePageAccessGuard('library');
  
  const { toast } = useToast();
  const navigate = useNavigate();
  const { selectedGame } = useSimplifiedUserData();

  // #region Store Operations
  /**
   * Library-specific store operations
   * These define how data is written to/from the objectsStore
   */
  const storeOperations = useMemo(() => ({
    writeToStore: (items: ObjectItem[]) => {
      objectsStore.setData(items);
    },
    updateInStore: (id: string, data: Partial<ObjectItem>) => {
      const current = objectsStore.getData();
      const updated = current.map(item => 
        item.id === id ? { ...item, ...data } : item
      );
      objectsStore.setData(updated);
    },
    deleteFromStore: (id: string) => {
      const current = objectsStore.getData();
      objectsStore.setData(current.filter(item => item.id !== id));
    },
  }), []);
  // #endregion

  // #region CRUD Handlers
  /**
   * Library-specific CRUD handlers
   * These handle server communication for library objects
   */
  const handlers = useMemo(() => ({
    onCreate: async (data: any, folderId?: string): Promise<ObjectItem> => {
      if (!selectedGame?.id) {
        throw new Error("No game selected");
      }

      const itemData = {
        game_id: data.game_id || selectedGame.id,
        name: data.name,
        type: "glossary",
        tags: data.tags || [],
        data: data.data as sectionDataObject[],
        blueprint_id: data.blueprint_id || null,
        blueprint_version: data.blueprint_version || 1,
        identifiers: data.identifiers || [],
        folder: folderId || data.folder || null,
      };

      const createdItem = await createObject(itemData, toast);
      
      // Update local store
      const current = objectsStore.getData();
      objectsStore.setData([...current, createdItem]);
      
      return createdItem;
    },

    onUpdate: async (id: string, data: Partial<ObjectItem>): Promise<ObjectItem> => {
      if (!selectedGame?.id) {
        throw new Error("No game selected");
      }

      const existingItem = objectsStore.getData().find(item => item.id === id);
      if (!existingItem) {
        throw new Error("Item not found");
      }

      const itemData = {
        id,
        game_id: data.game_id || selectedGame.id,
        name: data.name || existingItem.name,
        type: data.type || existingItem.type || "glossary",
        tags: data.tags || existingItem.tags || [],
        data: data.data || existingItem.data,
        blueprint_id: data.blueprint_id !== undefined ? data.blueprint_id : existingItem.blueprint_id,
        blueprint_version: data.blueprint_version || existingItem.blueprint_version,
        identifiers: data.identifiers || existingItem.identifiers || [],
        status: data.status || existingItem.status,
        owner: data.owner || existingItem.owner,
        edit_permissions: data.edit_permissions || existingItem.edit_permissions,
        description: data.description || existingItem.description,
        folder: data.folder !== undefined ? data.folder : existingItem.folder,
      };

      await updateObject(itemData, 'patch', undefined, toast);
      
      // Update local store
      storeOperations.updateInStore(id, itemData);
      
      return { ...existingItem, ...itemData } as ObjectItem;
    },

    onDelete: async (id: string): Promise<void> => {
      if (!selectedGame?.id) {
        throw new Error("No game selected");
      }

      await deleteObject(id, selectedGame.id, toast);
      storeOperations.deleteFromStore(id);
    },

    onBulkDelete: async (ids: string[]): Promise<void> => {
      if (!selectedGame?.id) {
        throw new Error("No game selected");
      }

      for (const id of ids) {
        await deleteObject(id, selectedGame.id, toast);
        storeOperations.deleteFromStore(id);
      }
    },
  }), [selectedGame, toast, storeOperations]);
  // #endregion

  // #region Card Transformation
  /**
   * Transform library items to CardData for display
   * Uses the DataSource transformer for consistency
   */
  const transformToCard = useCallback((item: ObjectItem, context: TransformContext): CardData => {
    // Use DataSource transformer with context for single source of truth
    const cards = objectsDataSource.transformers.toCard?.([item], {
      tags: context.tags,
      blueprints: context.blueprints || [],
      getUserNameById: context.getUserNameById,
    }) || [];
    
    return cards[0] || {
      id: item.id,
      title: item.name,
      gameId: item.game_id,
      folder: item.folder,
      tags: item.tags || [],
    };
  }, []);

  /**
   * Render a library card using GlossaryWidget
   */
  const renderCard = useCallback((card: CardData, tags: any[]) => {
    return (
      <GlossaryWidget
        id={card.id}
        gameId={card.gameId}
        title={card.title}
        owner={card.owner}
        tags={card.tags}
        description={card.description}
        lastUpdate={card.lastUpdate}
        version={card.version}
        status={card.status}
        folder={card.folder as string}
        itemType="library"
        icon={card.icon}
        iconBg={card.iconBg}
        shadowType={card.shadowType}
        shadowColor={card.shadowColor}
        hasVersionMismatch={card.hasVersionMismatch}
        onDelete={card.onDelete}
        onEdit={card.onEdit}
      />
    );
  }, []);
  // #endregion

  // #region Edit Handler
  /**
   * Handle edit action for library items
   */
  const onEdit = useCallback((id: string, data: ObjectItem[], navigateFn: (path: string) => void) => {
    // Navigate to library item detail page
    const item = data.find(item => item.id === id);
    navigate(`/library/${id}`, { state: { itemName: item?.name } });
  }, [navigate]);
  // #endregion

  // #region Wizard Callbacks
  /**
   * Library-specific wizard callbacks
   * Define what happens after wizard create/update operations
   */
  const wizardCallbacks = useMemo(() => ({
    onItemCreated: async (item: GlossaryItem) => {
      toast({
        title: "Success",
        description: `Library item "${item.name}" created successfully`,
      });
      
      // Refresh data from server to ensure sync
      if (selectedGame?.id) {
        try {
          await fetchObjects(selectedGame.id);
        } catch (error) {
          console.warn("Failed to refresh library items after creation:", error);
        }
      }
    },

    onItemUpdated: async (item: GlossaryItem) => {
      toast({
        title: "Success",
        description: `Library item "${item.name}" updated successfully`,
      });
      
      // Refresh data from server to ensure sync
      if (selectedGame?.id) {
        try {
          await fetchObjects(selectedGame.id);
        } catch (error) {
          console.warn("Failed to refresh library items after update:", error);
        }
      }
    },

    onWizardClose: () => {
      // Any cleanup needed when wizard closes
    },
  }), [toast, selectedGame]);
  // #endregion

  // #region Combined PageHandlers
  /**
   * Combine all handlers into PageHandlers interface
   */
  const pageHandlers: PageHandlers<ObjectItem> = useMemo(() => ({
    storeOperations,
    handlers,
    transformToCard,
    renderCard,
    wizardCallbacks,
    onEdit,
    features: {
      supportsPendingChanges: true,
      supportsBulkCreate: true,
      supportsFlowView: false,
    },
  }), [storeOperations, handlers, transformToCard, renderCard, wizardCallbacks, onEdit]);
  // #endregion

  return (
    <UnifiedItemsContainer 
      itemType="library"
      handlers={pageHandlers}
      config={libraryConfig}
    />
  );
}
